import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Input, InputNumber, Select, Switch, Button, Card, message, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getScoringRule,
  createScoringRule,
  updateScoringRule,
  SCORING_ENTITY_TYPES,
  SCORING_OPERATORS,
  SCORABLE_FIELDS,
} from '@/api/analyticsApi';
import AnalyticsNav from './AnalyticsNav';

export default function ScoringRuleForm() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entityType, setEntityType] = useState<string>('customer');

  const availableFields = SCORABLE_FIELDS[entityType] ?? [];

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const data = await getScoringRule(id);
      setEntityType(data.entity_type);
      form.setFieldsValue({
        name: data.name,
        description: data.description,
        entity_type: data.entity_type,
        field: data.field,
        operator: data.operator,
        value: data.value,
        score: data.score,
        priority: data.priority,
        is_active: data.is_active,
        category: data.category,
      });
    } catch {
      message.error(t('app.error'));
    } finally {
      setLoading(false);
    }
  }, [id, form, t]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleEntityTypeChange = (val: string) => {
    setEntityType(val);
    form.setFieldValue('field', undefined);
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    setSaving(true);
    try {
      if (isEdit) {
        await updateScoringRule(id!, {
          name: values.name as string,
          description: (values.description as string) || undefined,
          entity_type: values.entity_type as string,
          field: values.field as string,
          operator: values.operator as string,
          value: values.value as string,
          score: values.score as number,
          priority: values.priority as number,
          is_active: values.is_active as boolean,
          category: (values.category as string) || undefined,
        });
      } else {
        await createScoringRule({
          name: values.name as string,
          description: (values.description as string) || undefined,
          entity_type: values.entity_type as string,
          field: values.field as string,
          operator: values.operator as string,
          value: values.value as string,
          score: values.score as number,
          priority: values.priority as number,
          is_active: values.is_active as boolean,
          category: (values.category as string) || undefined,
        });
      }
      message.success(t('lead_scoring.rule_saved'));
      navigate('/analytics/scoring-rules');
    } catch {
      message.error(t('app.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      <AnalyticsNav />
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/analytics/scoring-rules')}
        style={{ marginBottom: 16 }}
      >
        {t('app.back')}
      </Button>

      <Card title={isEdit ? t('lead_scoring.edit_rule_title') : t('lead_scoring.create_rule_title')}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            is_active: true,
            entity_type: 'customer',
            score: 10,
            priority: 100,
          }}
        >
          <Form.Item
            name="name"
            label={t('lead_scoring.field.name')}
            rules={[{ required: true }]}
          >
            <Input maxLength={200} />
          </Form.Item>

          <Form.Item
            name="entity_type"
            label={t('lead_scoring.field.entity_type')}
            rules={[{ required: true }]}
          >
            <Select
              options={SCORING_ENTITY_TYPES.map((et) => ({
                value: et,
                label: t(`lead_scoring.entity_type.${et}`),
              }))}
              onChange={handleEntityTypeChange}
            />
          </Form.Item>

          <Form.Item
            name="field"
            label={t('lead_scoring.field.field')}
            rules={[{ required: true }]}
          >
            <Select
              showSearch
              options={availableFields.map((f) => ({
                value: f,
                label: f,
              }))}
              placeholder={t('lead_scoring.select_field_placeholder')}
            />
          </Form.Item>

          <Form.Item
            name="operator"
            label={t('lead_scoring.field.operator')}
            rules={[{ required: true }]}
          >
            <Select
              options={SCORING_OPERATORS.map((op) => ({
                value: op,
                label: t(`lead_scoring.operator.${op}`),
              }))}
            />
          </Form.Item>

          <Form.Item
            name="value"
            label={t('lead_scoring.field.value')}
            rules={[{ required: true }]}
            extra={t('lead_scoring.value_hint')}
          >
            <Input.TextArea rows={2} maxLength={10000} />
          </Form.Item>

          <Form.Item
            name="score"
            label={t('lead_scoring.field.score')}
            rules={[{ required: true }]}
            extra={t('lead_scoring.score_hint')}
          >
            <InputNumber min={-1000} max={1000} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="priority"
            label={t('lead_scoring.field.priority')}
            rules={[{ required: true }]}
            extra={t('lead_scoring.priority_hint')}
          >
            <InputNumber min={0} max={9999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item
            name="is_active"
            label={t('lead_scoring.field.is_active')}
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('lead_scoring.field.category')}
          >
            <Input maxLength={100} placeholder={t('lead_scoring.category_placeholder')} />
          </Form.Item>

          <Form.Item
            name="description"
            label={t('lead_scoring.field.description')}
          >
            <Input.TextArea rows={2} maxLength={2000} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>
              {t('app.save')}
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}
